import SmoothScroll from '../singleton/SmoothScroll';

type ShopifyBuyGlobal = {
  UI?: {
    onReady: (client: unknown) => Promise<ShopifyBuyUIInstance>;
  };
  buildClient: (config: { domain: string; storefrontAccessToken: string }) => unknown;
};

type ShopifyBuyUIInstance = {
  createComponent: (
    type: 'product',
    config: {
      id: string;
      node: HTMLElement;
      moneyFormat: string;
      options: Record<string, unknown>;
    },
  ) => void;
};

type ShopifyMount = {
  node: HTMLElement;
  productId: string;
};

type WindowWithShopify = Window & {
  ShopifyBuy?: ShopifyBuyGlobal;
};

const SHOPIFY_SCRIPT_URL =
  'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';
const SHOPIFY_DOMAIN = 'late-round-fantasy-football.myshopify.com';
const SHOPIFY_STOREFRONT_ACCESS_TOKEN = 'e8b7de1d0873ecca9f08e97687732347';
const SHOPIFY_MONEY_FORMAT = '%24%7B%7Bamount%7D%7D';
const SHOPIFY_PRODUCT_OPTIONS: Record<string, unknown> = {
  product: {
    iframe: false,
    layout: 'vertical',
    width: '100%',
  },
  cart: {
    iframe: false,
  },
  toggle: {
    iframe: false,
  },
};

export default class MerchShopify {
  private static sdkPromise: Promise<ShopifyBuyGlobal> | null = null;

  private mounts: ShopifyMount[];
  private refreshTimeoutId: number | null = null;
  private observedNodes = new WeakSet<HTMLElement>();

  constructor() {
    this.mounts = this.getMounts();

    if (!this.mounts.length) {
      return;
    }

    this.init();
  }

  private getMounts(): ShopifyMount[] {
    return Array.from(
      document.querySelectorAll<HTMLElement>('[data-shopify-product-id]'),
    )
      .map((node) => {
        const productId = node.dataset.shopifyProductId?.trim() ?? '';

        if (!productId) {
          return null;
        }

        return {
          node,
          productId,
        };
      })
      .filter((mount): mount is ShopifyMount => mount !== null);
  }

  private async init() {
    const ShopifyBuy = await MerchShopify.loadSdk();

    if (!ShopifyBuy.UI) {
      return;
    }

    const client = ShopifyBuy.buildClient({
      domain: SHOPIFY_DOMAIN,
      storefrontAccessToken: SHOPIFY_STOREFRONT_ACCESS_TOKEN,
    });

    const ui = await ShopifyBuy.UI.onReady(client);

    this.mounts.forEach((mount) => {
      this.observeMount(mount.node);

      ui.createComponent('product', {
        id: mount.productId,
        node: mount.node,
        moneyFormat: SHOPIFY_MONEY_FORMAT,
        options: SHOPIFY_PRODUCT_OPTIONS,
      });

      this.requestScrollRefresh();
    });
  }

  private observeMount(node: HTMLElement) {
    if (this.observedNodes.has(node)) {
      return;
    }

    this.observedNodes.add(node);

    const observer = new MutationObserver(() => {
      this.requestScrollRefresh();
    });

    observer.observe(node, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    node.addEventListener(
      'load',
      () => {
        this.requestScrollRefresh();
      },
      true,
    );
  }

  private requestScrollRefresh() {
    if (this.refreshTimeoutId !== null) {
      window.clearTimeout(this.refreshTimeoutId);
    }

    this.refreshTimeoutId = window.setTimeout(() => {
      this.refreshTimeoutId = null;
      SmoothScroll.refresh();
    }, 100);
  }

  private static loadSdk(): Promise<ShopifyBuyGlobal> {
    const shopifyWindow = window as WindowWithShopify;

    if (shopifyWindow.ShopifyBuy?.UI) {
      return Promise.resolve(shopifyWindow.ShopifyBuy);
    }

    if (MerchShopify.sdkPromise) {
      return MerchShopify.sdkPromise;
    }

    MerchShopify.sdkPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>(
        `script[src="${SHOPIFY_SCRIPT_URL}"]`,
      );

      const handleLoad = () => {
        const loadedShopify = (window as WindowWithShopify).ShopifyBuy;

        if (loadedShopify?.UI) {
          resolve(loadedShopify);
          return;
        }

        reject(new Error('Shopify Buy Button SDK loaded without UI support.'));
      };

      if (existingScript) {
        if (shopifyWindow.ShopifyBuy?.UI) {
          resolve(shopifyWindow.ShopifyBuy);
          return;
        }

        existingScript.addEventListener('load', handleLoad, { once: true });
        existingScript.addEventListener(
          'error',
          () => reject(new Error('Failed to load Shopify Buy Button SDK.')),
          { once: true },
        );
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = SHOPIFY_SCRIPT_URL;
      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener(
        'error',
        () => reject(new Error('Failed to load Shopify Buy Button SDK.')),
        { once: true },
      );

      (document.head || document.body).appendChild(script);
    });

    return MerchShopify.sdkPromise;
  }
}
